import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { NCRDispositionRegistry } from "@apv-erp/status";
import { trpcClient } from "@/lib/trpc";

export type NcrDispositionOption = { value: string; label: string; color?: string };

/** Dự phòng khi cấu hình hệ thống QC_DISPOSITION_OPTIONS chưa có */
const FALLBACK_COLORS: Record<string, string> = {
  REWORK: "border-amber-300 bg-amber-50 text-amber-900",
  SCRAP_REPRINT: "border-rose-300 bg-rose-50 text-rose-900",
  CONCESSION: "border-blue-300 bg-blue-50 text-blue-900",
  RETURN_VENDOR: "border-purple-300 bg-purple-50 text-purple-900",
  DOWNGRADE: "border-slate-300 bg-slate-100 text-slate-800",
};
const FALLBACK_OPTIONS: NcrDispositionOption[] = Object.keys(FALLBACK_COLORS).map((value, i) => ({
  value,
  label: `${i + 1}. ${NCRDispositionRegistry[value].label}`,
  color: FALLBACK_COLORS[value],
}));

/** Bỏ số thứ tự "1. " của nhãn dùng cho lựa chọn trên form */
const plainLabel = (label: string) => label.replace(/^\s*\d+\.\s*/, "");

/**
 * C-13: cách xử lý NCR — một nguồn nhãn duy nhất cho form, chi tiết, danh sách:
 * cấu hình hệ thống QC_DISPOSITION_OPTIONS (quản trị sửa được), thiếu thì dùng NCRDispositionRegistry.
 */
export function useNcrDispositions() {
  const { data: sysConfigs } = useQuery({
    queryKey: ["configs", "QC_DISPOSITION_OPTIONS"],
    queryFn: () => trpcClient.config.getAll.query(),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const configured = (sysConfigs as any)?.QC_DISPOSITION_OPTIONS;
    const options: NcrDispositionOption[] = Array.isArray(configured) && configured.length > 0 ? configured : FALLBACK_OPTIONS;
    const labelOf = (code?: string | null) => {
      if (!code) return "";
      const hit = options.find((o) => o.value === code);
      return hit ? plainLabel(hit.label) : NCRDispositionRegistry[code]?.label || code;
    };
    const colorOf = (code?: string | null) => (code && NCRDispositionRegistry[code]?.color) || "bg-slate-100 text-slate-600";
    return { options, labelOf, colorOf };
  }, [sysConfigs]);
}
